import { Controller } from "@hotwired/stimulus"

/**
 * Signature Management Controller
 * Handles signature loading, validation, and saving
 */
export default class extends Controller {
  // Debounce timeout for auto-save
  saveTimeout = null

  connect() {
    console.log("Signature controller connected")

    // Wait for select2 initialization and DOM to be fully ready
    setTimeout(() => {
      this.initializeSignatures()
      this.setupSelect2Handlers()
    }, 800)
  }

  setupSelect2Handlers() {
    // Listen to select2 change events specifically
    $('.find-user-certificate').on('select2:select select2:clear change', (e) => {
      const userId = $(e.target).val()
      const signatureId = $(e.target).data('signature')

      // Hide any error messages when user changes
      this.hideErrorMessages()

      // Always clear certificate, status, and signature when user changes
      this.clearCertificates(signatureId)
      this.clearSignature(signatureId)
      this.clearStatus(signatureId)

      if (userId) {
        // Load new certificates for the selected user
        this.loadUserCertificates(userId, signatureId, e.target)
        this.loadUserSignature(userId, signatureId)
      }

      this.checkApplyButtonState()
    })

    $('.certificate-list').on('select2:select select2:clear change', (e) => {
      const certificateId = $(e.target).val()
      const signatureId = $(e.target).data('signature')

      // Hide any error messages when certificate changes
      this.hideErrorMessages()

      // Find corresponding user select to get user_id
      const userSelect = $(`.find-user-certificate[data-signature="${signatureId}"]`)
      const userId = userSelect.val()

      if (certificateId && userId) {
        this.validateCertificate(certificateId, signatureId, userId)

        // Auto-save signatures when certificate is selected
        console.log('[signature] Certificate selected, auto-saving signature')
        this.autoSaveSignature()
      }
    })
  }

  hideErrorMessages() {
    // Hide the red error box at the top
    $('#report-error-box').addClass('hide')

    // Also hide any inline validation notices
    $('.alert[role="alert"]').not('#report-error-box').addClass('hide')
  }

  // Auto-save signature after certificate selection (debounced)
  autoSaveSignature() {
    console.log('[signature] Auto-saving signature (debounced)...')

    // Cancel any pending save
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }

    // Debounce: wait 500ms before saving
    this.saveTimeout = setTimeout(() => {
      this.saveSignatures()
        .then(() => {
          console.log('[signature] Auto-save successful')

          // Show success toast
          this.showSuccessToast('Signature saved successfully')

          // Dispatch event to notify tab navigation controller
          const event = new CustomEvent('signatures-updated', {
            bubbles: true,
            detail: { saved: true }
          })
          this.element.dispatchEvent(event)
        })
        .catch(error => {
          console.error('[signature] Auto-save failed:', error)
          // Show error but don't block user
          this.showErrorToast('Failed to save signature. Please try again.')
        })
    }, 500)
  }

  showSuccessToast(message) {
    // Create a simple toast notification
    const toast = $(`
      <div class="alert alert-success alert-dismissible fade show" role="alert" style="position: fixed; top: 20px; right: 20px; z-index: 9999; min-width: 300px;">
        <i class="fas fa-check-circle mr-2"></i>${message}
        <button type="button" class="close" data-dismiss="alert" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    `)
    $('body').append(toast)

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      toast.alert('close')
    }, 3000)
  }

  showErrorToast(message) {
    // Create a simple error toast notification
    const toast = $(`
      <div class="alert alert-danger alert-dismissible fade show" role="alert" style="position: fixed; top: 20px; right: 20px; z-index: 9999; min-width: 300px;">
        <i class="fas fa-exclamation-circle mr-2"></i>${message}
        <button type="button" class="close" data-dismiss="alert" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    `)
    $('body').append(toast)

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      toast.alert('close')
    }, 5000)
  }

  // Stimulus action handler (for non-select2 compatibility)
  handleSelectChange(event) {
    console.log('Select change detected', event.target)
    // This will be handled by select2 handlers above
  }

  initializeSignatures() {
    console.log('Initializing signatures...')

    // Load certificates and signatures for pre-selected users
    $('.find-user-certificate').each((index, select) => {
      const userId = $(select).val()
      if (userId) {
        const signatureId = $(select).data('signature')
        console.log(`Loading for user ${userId}, signature ${signatureId}`)
        this.loadUserCertificates(userId, signatureId, select)
        this.loadUserSignature(userId, signatureId)
      }
    })

    // Check if we have any signatures to enable apply button
    this.checkApplyButtonState()
  }

  loadUserCertificates(userId, signatureId, userSelect) {
    console.log(`Loading certificates for user ${userId}, signature ${signatureId}`)

    $.ajax({
      url: `/users/${userId}/certificates`,
      method: 'GET',
      dataType: 'json',
      success: (response) => {
        // Response is an array of certificates directly
        this.populateCertificates(response, signatureId, userSelect)
      },
      error: (xhr, status, error) => {
        console.error('Failed to load certificates:', error)
      }
    })
  }

  populateCertificates(certificates, signatureId, userSelect) {
    const certificateSelect = $(`.certificate-list.js-${signatureId}`)

    // Clear existing options except the blank option
    certificateSelect.find('option:not([value=""])').remove()

    // Add certificate options with validity status
    certificates.forEach(cert => {
      // Check validity and add status to name
      let displayName = cert.name
      const expiryDate = cert.expiry_date ? new Date(cert.expiry_date) : null

      if (expiryDate) {
        const today = new Date()
        if (expiryDate < today) {
          displayName += ' (Expired)'
        } else {
          displayName += ' (Valid)'
        }
      }

      const option = new Option(displayName, cert.id, false, false)
      certificateSelect.append(option)
    })

    // Don't auto-select previously selected certificate - user must select manually
    // The certificateSelect.data('certificate') is kept for reference but not used for auto-selection

    // Reinitialize select2
    if (certificateSelect.hasClass('select2-hidden-accessible')) {
      certificateSelect.select2('destroy')
    }
    certificateSelect.select2()
  }

  loadUserSignature(userId, signatureId) {
    console.log(`Loading signature for user ${userId}, signature ${signatureId}`)

    $.ajax({
      url: `/users/${userId}`,
      method: 'GET',
      dataType: 'json',
      success: (response) => {
        if (response.signature_url) {
          this.displaySignature(response.signature_url, signatureId)
        } else {
          this.showNoSignature(signatureId)
        }
      },
      error: (xhr, status, error) => {
        console.error('Failed to load signature:', error)
        this.showNoSignature(signatureId)
      }
    })
  }

  displaySignature(signatureUrl, signatureId) {
    console.log(`Displaying signature for ${signatureId}`)

    // Try to find existing signature image
    let signatureImg = $(`.img_preview.js-${signatureId}`)

    if (signatureImg.length > 0) {
      // Update existing image
      signatureImg.attr('src', signatureUrl)
    } else {
      // Find the table row for this signature
      const userSelect = $(`.find-user-certificate[data-signature="${signatureId}"]`)
      const row = userSelect.closest('tr')

      // Find the signature cell (5th td: Role, Inspector, Certificate, Status, Signature)
      const signatureCell = row.find('td').eq(4)

      // Replace content with signature image
      signatureCell.html(
        `<img src="${signatureUrl}" class="img_preview js-${signatureId}" alt="Signature" style="max-height: 50px; max-width: 80px; border: 1px solid #dee2e6; border-radius: 4px; padding: 2px;">`
      )
    }
  }

  showNoSignature(signatureId) {
    console.log(`Showing no signature for ${signatureId}`)

    // Find the signature image
    const signatureImg = $(`.img_preview.js-${signatureId}`)

    if (signatureImg.length > 0) {
      // Get the parent cell and replace content
      const cell = signatureImg.closest('td')
      cell.html(`
        <div class="text-muted small">
          <i class="fa fa-exclamation-circle mr-1"></i>
          No signature
        </div>
      `)
    } else {
      // Image doesn't exist, but we should still ensure "No signature" is shown
      const userSelect = $(`.find-user-certificate[data-signature="${signatureId}"]`)
      const row = userSelect.closest('tr')

      // Find the signature cell (5th td: Role, Inspector, Certificate, Status, Signature)
      const signatureCell = row.find('td').eq(4)

      signatureCell.html(`
        <div class="text-muted small">
          <i class="fa fa-exclamation-circle mr-1"></i>
          No signature
        </div>
      `)
    }
  }

  clearSignature(signatureId) {
    this.showNoSignature(signatureId)
  }

  clearCertificates(signatureId) {
    const certificateSelect = $(`.certificate-list.js-${signatureId}`)

    // Remove all options except the blank placeholder
    certificateSelect.find('option:not([value=""])').remove()

    // Set to empty value
    certificateSelect.val(null)

    // Destroy and reinitialize select2 to reset properly
    if (certificateSelect.hasClass('select2-hidden-accessible')) {
      certificateSelect.select2('destroy')
    }
    certificateSelect.select2()

    console.log(`[signature] Cleared certificates for signature ${signatureId}`)
  }

  clearStatus(signatureId) {
    const statusCell = $(`.status-cell-${signatureId}`)
    statusCell.html('<div class="text-muted small">-</div>')
  }

  validateCertificate(certificateId, signatureId, userId) {
    console.log(`Validating certificate ${certificateId}`)

    $.ajax({
      url: `/users/${userId}/certificates_users/${certificateId}/validate_certificate`,
      method: 'GET',
      data: { user_id: userId, certificate_id: certificateId },
      dataType: 'json',
      success: (response) => {
        // Update status column
        this.updateStatusColumn(signatureId, response.success)

        // Only show warning if certificate is expired
        if (!response.success) {
          const userSelect = $(`.find-user-certificate[data-signature="${signatureId}"]`)
          const index = $('.find-user-certificate').index(userSelect)
          const noticeBox = $(`.notice-${index}`)

          noticeBox.removeClass('alert-success').addClass('alert-danger')
          noticeBox.text(response.message || 'Certificate has expired').removeClass('hide')

          // Hide notice after 2 seconds
          setTimeout(() => {
            noticeBox.addClass('hide')
          }, 2000)
        }
        // No notification for valid certificates since it's shown in dropdown
      },
      error: (xhr, status, error) => {
        console.error('Failed to validate certificate:', error)
      }
    })
  }

  updateStatusColumn(signatureId, isValid) {
    const statusCell = $(`.status-cell-${signatureId}`)

    if (isValid) {
      statusCell.html(`
        <div class="badge badge-success">
          <i class="fa fa-check-circle mr-1"></i>
          Valid
        </div>
      `)
    } else {
      statusCell.html(`
        <div class="badge badge-danger">
          <i class="fa fa-times-circle mr-1"></i>
          Expired
        </div>
      `)
    }
  }

  // Clear a specific signature row - deletes ALL signatures from the server
  async clearSignatureRow(event) {
    event.preventDefault()
    const signatureId = event.currentTarget.dataset.signature

    console.log(`Clearing signature row for signature ${signatureId}`)

    // Hide error messages when clearing
    this.hideErrorMessages()

    // Get task and report IDs for the AJAX call
    const taskId = $('#task_id').val()
    const reportId = $('#report_id').val()

    // First, delete all signatures from the server
    try {
      const response = await fetch(`/reports/tasks/${taskId}/reports/${reportId}/report_signatures/destroy_all`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.csrfToken(),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log('[signature] Server response:', data)

      // Now clear all signature rows in the UI
      $('.find-user-certificate').each((index, select) => {
        const sigId = $(select).data('signature')

        // Clear user select
        $(select).val('').trigger('change.select2')

        // Clear certificate select
        const certificateSelect = $(`.certificate-list.js-${sigId}`)
        certificateSelect.find('option:not([value=""])').remove()
        certificateSelect.val('').trigger('change.select2')

        // Clear signature image
        this.showNoSignature(sigId)

        // Clear status column
        this.clearStatus(sigId)

        // Clear the hidden report_signature_id field since we deleted all signatures
        $(`input[name="report[report_signature_id][${index}]"]`).val('')
      })

      // Trigger a change event on the form to mark as changed
      $('#msform').trigger('change')

      // Update apply button state
      this.checkApplyButtonState()

      // Show success toast
      this.showSuccessToast('All signatures cleared successfully')

      console.log('[signature] All signatures cleared from server and UI')

    } catch (error) {
      console.error('[signature] Failed to clear signatures:', error)
      this.showErrorToast('Failed to clear signatures. Please try again.')
    }
  }

  checkApplyButtonState() {
    // Check if there are any changes to save:
    // 1. At least one user AND certificate is selected (new or existing signature)
    // 2. OR a signature is pending deletion (has report_signature_id but no user/cert)

    let hasCompleteSelection = false
    let hasPendingDeletion = false

    $('.find-user-certificate').each(function(index) {
      const userId = $(this).val()
      const signatureId = $(this).data('signature')
      const certificateSelect = $(`.certificate-list.js-${signatureId}`)
      const certificateId = certificateSelect.val()

      // Check for complete selections (new or update)
      if (userId && certificateId) {
        hasCompleteSelection = true
      }

      // Check for pending deletions (has report_signature_id but no user/certificate)
      const reportSignatureId = $(`input[name="report[report_signature_id][${index}]"]`).val()
      if (reportSignatureId && reportSignatureId !== '' && (!userId || !certificateId)) {
        hasPendingDeletion = true
        console.log(`Found pending deletion for signature ${reportSignatureId} at index ${index}`)
      }
    })

    const applyButton = $('#apply-signatures-button')
    console.log('Button state check - complete selections:', hasCompleteSelection, ', pending deletions:', hasPendingDeletion)

    // Enable button if there are complete selections OR pending deletions
    if (hasCompleteSelection || hasPendingDeletion) {
      applyButton.prop('disabled', false)
    } else {
      applyButton.prop('disabled', true)
    }
  }

  // Handle apply signatures button click
  applySignatures(event) {
    event.preventDefault()

    this.saveSignatures()
      .then(() => {
        // Dispatch custom event to notify tab navigation controller
        const event = new CustomEvent('signatures-updated', {
          bubbles: true,
          detail: { saved: true }
        })
        this.element.dispatchEvent(event)

        // Success message is shown by create.js.erb
        // User stays on signature tab and can manually click Preview when ready
        console.log('Signatures saved successfully. User remains on signature tab.')
      })
      .catch(error => {
        console.error('Failed to save signatures:', error)
        alert('Failed to save signatures. Please try again.')
      })
  }

  // Save all signatures via AJAX
  saveSignatures() {
    return new Promise((resolve, reject) => {
      $('.loader').show()
      $('.content-wrapper').addClass('blur-all')

      const taskId = $('#task_id').val()
      const reportId = $('#report_id').val()

      // Initialize data structures matching what controller expects
      const reportData = {
        certificate_hash: {},
        user_id: {},
        certificate_id: {},
        signature_mandate_type: {},
        report_signature_id: {},
        signature_requirement_id: {}
      }

      // Collect all signature data
      $('select[name^="report[user_id]"]').each(function(index) {
        const userId = $(this).val()
        const certificateId = $(`select[name="report[certificate_id][${index}]"]`).val()
        const mandateType = $(`input[name="report[signature_mandate_type][${index}]"]`).val()
        const signatureId = $(`input[name="report[report_signature_id][${index}]"]`).val()
        const signatureRequirementId = $(`input[name="report[signature_requirement_id][${index}]"]`).val()

        // Always send the data for this index (even if blank for deletion)
        reportData.user_id[index] = userId || ''
        reportData.certificate_id[index] = certificateId || ''
        reportData.signature_mandate_type[index] = mandateType
        reportData.report_signature_id[index] = signatureId || ''
        reportData.signature_requirement_id[index] = signatureRequirementId || ''

        if (userId && certificateId) {
          // Normal signature - include in certificate_hash
          reportData.certificate_hash[index] = certificateId
          console.log(`Signature ${index}: user=${userId}, cert=${certificateId}, sig_id=${signatureId}`)
        } else if (signatureId && signatureId !== '') {
          // Signature was cleared but had an ID (existed before)
          console.log(`Marking signature ${signatureId} at index ${index} for deletion`)
        }
      })

      console.log('Sending signature data:', reportData)

      // Get override checkbox value (for admin/super_admin users)
      const overrideExpired = $('#override_expired').is(':checked') ? 'true' : 'false'
      console.log('[Signatures] Override expired checkbox checked:', $('#override_expired').is(':checked'))
      console.log('[Signatures] Override expired value being sent:', overrideExpired)

      $.ajax({
        url: `/reports/tasks/${taskId}/reports/${reportId}/report_signatures`,
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken()
        },
        data: {
          report: reportData,
          task_id: taskId,
          override_expired: overrideExpired
        },
        dataType: 'script',  // Expects .js.erb response
        success: (response) => {
          $('.loader').hide()
          $('.content-wrapper').removeClass('blur-all')

          console.log('Signatures saved successfully')
          resolve(response)
        },
        error: (xhr, status, error) => {
          $('.loader').hide()
          $('.content-wrapper').removeClass('blur-all')

          console.error('Signature save failed:', xhr.status, error)
          console.error('Response:', xhr.responseText)
          reject(error)
        }
      })
    })
  }

  // Get CSRF token from meta tag
  csrfToken() {
    const element = document.querySelector('meta[name="csrf-token"]')
    return element ? element.content : ''
  }

  // Save signatures and navigate to preview tab
  async saveAndPreview(event) {
    event.preventDefault()
    console.log('[signature] saveAndPreview called')

    // Disable button to prevent double-clicks
    const button = event.currentTarget
    const originalHTML = button.innerHTML
    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Saving...'

    try {
      // Save signatures
      await this.saveSignatures()

      console.log('[signature] Save successful, navigating to preview tab')

      // Dispatch event to notify tab navigation controller
      const event = new CustomEvent('signatures-updated', {
        bubbles: true,
        detail: { saved: true }
      })
      this.element.dispatchEvent(event)

      // Navigate to preview tab
      this.navigateToPreviewTab()
    } catch (error) {
      console.error('[signature] Save failed:', error)

      // Re-enable button
      button.disabled = false
      button.innerHTML = originalHTML

      // Show error message
      alert(`Failed to save signatures: ${error}. Please try again.`)
    }
  }

  navigateToPreviewTab() {
    console.log('[signature] Navigating to preview tab')

    // Clear any hash from URL to prevent interference
    if (window.location.hash) {
      console.log('[signature] Clearing hash before navigation:', window.location.hash)
      const cleanUrl = window.location.pathname + window.location.search
      window.history.replaceState(null, '', cleanUrl)
    }

    // Small delay to ensure DOM updates from signature save have completed
    setTimeout(() => {
      // Find the tab navigation controller
      const tabNavController = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller~="tab-navigation"]'),
        'tab-navigation'
      )

      if (!tabNavController) {
        console.error('[signature] Tab navigation controller not found')
        return
      }

      // Ensure preview tab is enabled (signatures were just saved)
      console.log('[signature] Enabling preview tab')
      tabNavController.hasSignaturesValue = true
      tabNavController.updatePreviewTabState()

      // Find the preview tab and perform tab switch
      const previewTab = tabNavController.tabTargets.find(tab => tab.id === 'preview')
      if (previewTab) {
        console.log('[signature] Found preview tab, performing tab switch')

        // Get the fieldset ID for preview tab
        const previewFieldsetId = tabNavController.getFieldsetId(previewTab)
        console.log('[signature] Preview fieldset ID:', previewFieldsetId)

        // Ensure tab is not disabled
        previewTab.classList.remove('disable')

        // Perform the tab switch directly
        tabNavController.performTabSwitch(previewFieldsetId, previewTab)

        console.log('[signature] Tab switch completed')
      } else {
        console.error('[signature] Preview tab not found')
        console.log('[signature] Available tabs:', tabNavController.tabTargets.map(t => t.id))
      }
    }, 500)
  }
}
